/********************************************************************************
 * Copyright (c) 2019 TypeFox and others
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License v. 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 ********************************************************************************/
import { isError } from './extension-registry-types';
/** Whether a rejection is the registry's 404, for a page that renders "not found" itself. */
export const isNotFound = (error) => error?.status === 404;
/** Whether a rejection is the registry's 401, which it answers when a request needs a login and nobody is logged in. */
export const isUnauthorized = (error) => error?.status === 401;
/**
 * Resolves with the parsed body of a 2xx response and rejects with an {@link ErrorResponse}
 * otherwise, including a 200 whose body is an error result, so a caller never checks
 * {@link isError} itself. It never retries: the query client owns retries, a 429 included.
 */
export async function sendRequest(req) {
    req.method ?? (req.method = 'GET');
    req.headers ?? (req.headers = {});
    if (!req.headers['Accept']) {
        req.headers['Accept'] = 'application/json';
    }
    const param = {
        method: req.method,
        signal: req.signal
    };
    if (req.payload) {
        param.body =
            req.payload instanceof File || req.payload instanceof FormData ? req.payload : JSON.stringify(req.payload);
    }
    param.headers = req.headers;
    if (req.followRedirect) {
        param.redirect = 'follow';
    }
    if (req.credentials) {
        param.credentials = 'include';
    }
    const response = await fetch(req.endpoint, param);
    if (response.ok) {
        const body = await parseBody(response, req.headers['Accept']);
        if (isError(body)) {
            // Carries its status like any other failure, so the query client won't retry an answer.
            throw { ...body, status: response.status };
        }
        return body;
    }
    let err;
    try {
        err = await response.json();
        err.status = response.status;
    }
    catch (_) {
        err = {
            error: `Request failed: ${req.method} ${response.url}`,
            status: response.status,
            message: response.statusText || `Status ${response.status}`
        };
    }
    if (response.status === 429) {
        // The bucket4j starter config sends the first header, dynamic rate limiting the second.
        const retryAfter = response.headers.get('X-Rate-Limit-Retry-After-Seconds') ?? response.headers.get('Retry-After');
        err.retryAfter = Number(retryAfter) || 0;
    }
    throw err;
}
function parseBody(response, accept) {
    switch (accept) {
        case 'application/json':
            return response.json();
        case 'text/plain':
            return response.text();
        case 'application/octet-stream':
            return response.blob();
        default:
            throw new Error(`Unsupported type ${accept}`);
    }
}
//# sourceMappingURL=server-request.js.map